import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, DataTable, EmptyState, Pagination, Pill, SelectField, Tabs, type Column } from "@atlas/ui/web";
import type { RetornoConsignatariaBanco } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import { fmtBRL } from "../../lib/banco-propostas";
import { useFiltros } from "../../lib/use-filtros";
import { bancoPermissaoCobre, readBancoPermissoesDaSessao } from "../../lib/banco-perms";
import { downloadAuthed } from "../prefeitura/_ui";
import { BancoEnvioArquivo } from "./envio-arquivo";

type Aba = "retornos" | "envio";

const ABAS: { key: Aba; label: string }[] = [
  { key: "retornos", label: "Baixar retornos" },
  { key: "envio", label: "Enviar arquivo mensal" },
];

/** AAAAMM → MM/AAAA. */
const competenciaBr = (c: string): string => (/^\d{6}$/.test(c) ? `${c.slice(4)}/${c.slice(0, 4)}` : c);

const dataBr = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
};

function tamanho(bytes: number | null): string {
  if (bytes == null) return "—";
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

/**
 * Troca de arquivos do banco. BAIXAR os retornos que a averbadora gerou para ele,
 * depois que o orgao subiu o retorno da folha; e, com a permissao
 * `arquivos-envio`, ENVIAR o arquivo mensal aos orgaos vinculados (fase 2).
 */
export function BancoTrocaArquivos() {
  // O envio e' escrita e tem recurso proprio (`arquivos-envio`); a leitura dos retornos segue `retornos`.
  const podeEnviar = bancoPermissaoCobre(readBancoPermissoesDaSessao(), "arquivos-envio");
  const [aba, setAba] = useState<Aba>("retornos");
  const [competencia, setCompetencia] = useState("");
  const [unidadeId, setUnidadeId] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(10);

  const consulta = useQuery({
    queryKey: ["banco", "retornos", competencia, unidadeId, pagina, porPagina],
    queryFn: () =>
      atlas.banco.retornos.listar({
        competencia: competencia || undefined,
        unidadeId: unidadeId ? Number(unidadeId) : undefined,
        offset: (pagina - 1) * porPagina,
        limit: porPagina,
      }),
    placeholderData: (anterior) => anterior,
  });
  const dados = consulta.data;
  const filtros = useFiltros(
    [
      { nome: "competencia", ligado: competencia !== "", limpar: () => setCompetencia("") },
      { nome: "orgao", ligado: unidadeId !== "", limpar: () => setUnidadeId("") },
    ],
    setPagina,
  );
  const { filtrar } = filtros;

  const baixar = (r: RetornoConsignatariaBanco, formato: "txt" | "xlsx") =>
    downloadAuthed(
      atlas.banco.retornos.arquivoUrl(r.id, formato),
      formato === "xlsx" ? (r.arquivoNome ?? "retorno.txt").replace(/\.txt$/i, ".xlsx") : (r.arquivoNome ?? "retorno.txt"),
    );

  const columns: Column<RetornoConsignatariaBanco>[] = [
    { key: "competencia", header: "Competência", render: (r) => competenciaBr(r.competencia) },
    { key: "orgao", header: "Órgão/entidade", wrap: true },
    {
      key: "arquivo",
      header: "Arquivo",
      render: (r) => (
        <span title={r.layout ? `${r.layout.nome} v${r.layout.versao}` : undefined}>
          {r.arquivoNome ?? "—"} <span style={{ color: "var(--text-dim)" }}>({tamanho(r.arquivoBytes)})</span>
        </span>
      ),
    },
    { key: "linhas", header: "Linhas", align: "right" },
    { key: "descontado", header: "Descontado", align: "right", render: (r) => fmtBRL(r.descontadoCentavos / 100) },
    { key: "geradoEm", header: "Gerado em", render: (r) => dataBr(r.geradoEm) },
    {
      key: "acoes",
      header: "",
      align: "right",
      render: (r) =>
        r.temArquivo ? (
          <span style={{ display: "inline-flex", gap: 8 }}>
            <Button variant="ghost" onClick={() => baixar(r, "txt")}>Baixar TXT</Button>
            {r.temPlanilha ? <Button variant="ghost" onClick={() => baixar(r, "xlsx")}>Baixar Excel</Button> : null}
          </span>
        ) : (
          <Pill variant="pendente">Sem arquivo</Pill>
        ),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Consignatária
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Troca de arquivos</h1>
        <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 680 }}>
          Retornos gerados para a consignatária depois que o órgão envia o retorno da folha. Cada arquivo diz o que foi
          descontado nas suas consignações naquela competência.
        </p>
      </header>

      {/* Sem o recurso de envio nao ha' o que separar: so' a aba de download, sem a barra. */}
      {podeEnviar ? <Tabs tabs={ABAS} activeKey={aba} onChange={(k) => setAba(k as Aba)} /> : null}

      {aba === "retornos" ? (
        <>
      <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
        <SelectField
          label="Competência"
          value={competencia}
          onChange={(e) => filtrar(setCompetencia)(e.target.value)}
          options={[{ value: "", label: "Todas" }, ...(dados?.facetas.competencias ?? []).map((c) => ({ value: c, label: competenciaBr(c) }))]}
        />
        <SelectField
          label="Órgão/entidade"
          value={unidadeId}
          onChange={(e) => filtrar(setUnidadeId)(e.target.value)}
          options={[{ value: "", label: "Todos" }, ...(dados?.facetas.orgaos ?? []).map((o) => ({ value: String(o.id), label: o.nome }))]}
        />
      </div>

      {consulta.isError ? (
        <div role="alert" style={{ padding: 12, borderRadius: 10, border: "1px solid var(--danger-500)", fontSize: 13 }}>
          Não foi possível carregar os arquivos: {(consulta.error as Error).message}
        </div>
      ) : null}

      {consulta.isLoading ? (
        <div style={{ color: "var(--text-dim)", fontSize: 14 }}>Carregando…</div>
      ) : (
        <>
          <DataTable columns={columns} rows={dados?.itens ?? []} rowKey={(r) => r.id} emptyState={
              <EmptyState
                motivo={dados && dados.total === 0 ? (filtros.temFiltro ? "filtro-vazio" : "sem-dados") : null}
                semDados="Nenhum retorno gerado para a sua consignatária ainda. Eles aparecem aqui quando a averbadora os gera."
                onLimpar={filtros.limparFiltros}
              />
            } />
          <Pagination
            total={dados?.total ?? 0}
            pagina={pagina}
            porPagina={porPagina}
            onPagina={setPagina}
            onPorPagina={(n) => {
              setPorPagina(n);
              setPagina(1);
            }}
            rotulo="arquivos"
          />
        </>
      )}
        </>
      ) : null}

      {aba === "envio" && podeEnviar ? <BancoEnvioArquivo /> : null}
    </div>
  );
}
